import { Avatar, Text, Menu, MenuTrigger, MenuList, MenuItem, MenuPopover, Button } from '@fluentui/react-components';
import {
    GridDots20Regular,
    Settings20Regular,
    PeopleTeam20Regular,
    ReadingModeMobile20Regular,
    AddCircle20Filled,
    ChevronDown12Regular,
    Sparkle20Filled,
    Sparkle16Filled,
} from '@fluentui/react-icons';
import styles from './index.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { GetProjectData, resetProjectData, selectProject, setProjectImg } from '../../../redux/projectDataSlice';
import { useLocation, useNavigate } from 'react-router';
import Cookies from 'js-cookie';
import { resetDocumentData, setDocs } from '../../../redux/documentsSlice';
import { logoutWithAudit, setActiveWorkspaceId, setWorkspaceSelectionRequired } from '../../../redux/usersSlice';
import { clearPulseModalSessionFlag } from '../../Pulse/PulseModalContext';
import logo from '../../../assets/longLogoBright.png';
import { resetObservationData } from '../../../redux/meetingSlice';
import { resetDataroomData } from '../../../redux/dataroomSlice';
import { resetNdaData } from '../../../redux/ndaSlice';
import { resetQestionBankData } from '../../../redux/questionBankSlice';
import { resetTaskData } from '../../../redux/taskSlice';
import { resetTeamData } from '../../../redux/teamSlice';
import React from 'react';
import { isLensIntelligenceEnabled } from '../../../utils/lensFeatures';
import { NotepadSidebar } from '../HeaderSidebars';
import PulseNavMenu from '../../Pulse/PulseNavButton';
import { usePulseModal } from '../../Pulse/PulseModalContext';
import { ObservationFormSidebar } from '../../Observations';
import { PostInformationRequestSidebar } from '../../IRL';
import TaskFormSidebar from '../../TaskManagement/TaskFormSidebar';
import GeneralGPTChatDrawer from '../../UserSettings/GeneralGPTChatDrawer';
import { useAiWarden } from '../../AiWarden';
import { useWorkspacePulseAccess } from '../../../hooks/useWorkspacePulseAccess';
import { openCookiePreferences } from '../../../utils/openCookiePreferences';
const Header = ({ hideProjectItems, minimal = false }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state?.projectData);
    const { user } = useSelector(state => state?.user);
    const memberships = useSelector(state => state?.user?.memberships || []);
    const activeWorkspaceIdFromState = useSelector(state => state?.user?.activeWorkspaceId);
    const fallbackWorkspaceId = !activeWorkspaceIdFromState && memberships && memberships.length === 1 ? memberships[0]?.workspace?.id : null;
    const effectiveWorkspaceId = activeWorkspaceIdFromState || fallbackWorkspaceId;
    const activeMembership = memberships.find(m => m?.workspace?.id === effectiveWorkspaceId) || (memberships && memberships.length === 1 ? memberships[0] : undefined);
    const isWorkspaceSuperAdmin = activeMembership?.role === 'WORKSPACE_SUPER_ADMIN';
    const { canView: canViewWorkspacePulse } = useWorkspacePulseAccess(effectiveWorkspaceId);
    const isProjectAdmin = selectedProject?.vdr?.vdr_admin === user?.id || selectedProject?.project_admins?.some(admin => admin.user_id === user?.id);

    const [notepadModal, setNotepadModal] = React.useState(false);
    const [questionModal, setQuestionModal] = React.useState(false);
    const [obsModal, setObsModal] = React.useState(false);
    const [taskModal, setTaskModal] = React.useState(false);
    const [showGeneralChat, setShowGeneralChat] = React.useState(false);
    const { openAiWarden } = useAiWarden();

    const { openModal, rolloutEnabled } = usePulseModal();

    const showUserPulse = rolloutEnabled;
    const showWorkspacePulse = canViewWorkspacePulse && Boolean(effectiveWorkspaceId);
    const showPulseMenu = showUserPulse || showWorkspacePulse;

    const handleOpenWorkspacePulse = () => {
        navigate(`/workspace-pulse?workspace_id=${encodeURIComponent(effectiveWorkspaceId)}`);
    };

    const isLensIntelligenceEnabledFlag = React.useMemo(() => isLensIntelligenceEnabled(selectedProject), [selectedProject]);

    const handleNavigateToProject = () => {
        dispatch(selectProject({ id: null }));
        dispatch(GetProjectData());
        navigate('/project');
        dispatch(resetObservationData());
        dispatch(resetDataroomData());
        dispatch(resetDocumentData());
        dispatch(resetNdaData());
        dispatch(resetProjectData());
        dispatch(resetQestionBankData());
        dispatch(resetTaskData());
        dispatch(resetTeamData());
    };
    const handleLogout = async () => {
        clearPulseModalSessionFlag();
        localStorage.removeItem('persist:root');
        Cookies.set('refresh', '');
        dispatch(setProjectImg(''));
        dispatch(setDocs([]));
        dispatch(resetQestionBankData()); // Reset IRL AI responses on logout
        await dispatch(logoutWithAudit());
        navigate('/');
    };

    const handleChangeWorkspace = () => {
        // Force workspace selection: user should not be able to navigate elsewhere until they pick.
        try {
            localStorage.removeItem('project_id');
        } catch (e) {}

        dispatch(setActiveWorkspaceId(''));
        dispatch(setWorkspaceSelectionRequired(true));
        dispatch(selectProject({ id: null }));
        dispatch(resetObservationData());
        dispatch(resetDataroomData());
        dispatch(resetDocumentData());
        dispatch(resetNdaData());
        dispatch(resetProjectData());
        dispatch(resetQestionBankData());
        dispatch(resetTaskData());
        dispatch(resetTeamData());

        navigate('/select-workspace');
    };

    if (minimal) {
        return (
            <>
                <div className={styles.header}>
                    <div className={styles.leftContent}>
                        <img onClick={() => navigate('/project')} role="button" src={logo} alt="lens-logo" className={styles.lensLogo} />
                    </div>
                    <div className="d-flex align-items-center">
                        {showPulseMenu ? (
                            <PulseNavMenu
                                showUserPulse={showUserPulse}
                                showWorkspacePulse={showWorkspacePulse}
                                onUserPulse={openModal}
                                onWorkspacePulse={handleOpenWorkspacePulse}
                            />
                        ) : null}
                        <Menu>
                            <MenuTrigger disableButtonEnhancement>
                                <Avatar role="button" name={user?.full_name} aria-label="Guest" active="active" color="beige" />
                            </MenuTrigger>
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem onClick={() => navigate('/user-settings')}>User Settings</MenuItem>
                                    {isWorkspaceSuperAdmin && effectiveWorkspaceId && (
                                        <MenuItem onClick={() => navigate(`/workspace-settings/${effectiveWorkspaceId}`)}>Workspace Settings</MenuItem>
                                    )}
                                    <MenuItem
                                        onClick={() => {
                                            setShowGeneralChat(true);
                                        }}>
                                        Ask Lens AI
                                    </MenuItem>
                                    <MenuItem>
                                        <a href="mailto:LensSupport@beyond-ma.com" style={{ textDecoration: 'none', color: 'inherit' }}>
                                            Contact Support
                                        </a>
                                    </MenuItem>
                                    <MenuItem onClick={openCookiePreferences}>Cookie preferences</MenuItem>
                                    {memberships.length > 1 && <MenuItem onClick={handleChangeWorkspace}>Change Workspace</MenuItem>}
                                    <MenuItem onClick={handleLogout}>Logout </MenuItem>
                                </MenuList>
                            </MenuPopover>
                        </Menu>
                    </div>
                </div>
                <GeneralGPTChatDrawer show={showGeneralChat} setShow={setShowGeneralChat} />
            </>
        );
    }
    return (
        <>
            <div className={styles.header}>
                <div className={styles.leftContent}>
                    {!hideProjectItems && <GridDots20Regular onClick={handleNavigateToProject} role="button" color="white" />}

                    <img onClick={handleNavigateToProject} role="button" src={logo} alt="lens-logo" className={styles.lensLogo} />

                    {!minimal && selectedProject?.project_name && (
                        <div className={styles.inputNameDiv}>
                            {' '}
                            <Text onClick={() => navigate(user?.has_diligence_tools ? '/overview' : '/dataroom')} role="button" as="p" font="Inter" className={styles.projectName}>
                                {selectedProject?.project_name}
                            </Text>
                            {/* {!hideProjectItems && (
                                <Input
                                    style={{ borderRadius: 16 }}
                                    appearance="filled-lighter"
                                    contentBefore={<Search20Regular aria-label="Enter by voice" />}
                                    className={styles.searchInput}
                                />
                            )} */}
                        </div>
                    )}
                </div>
                <div>
                    {!hideProjectItems && (
                        <>
                            {selectedProject?.id ? (
                                <>
                                    <Sparkle20Filled role="button" onClick={openAiWarden} className="me-2" color="var(--Brand-Primary)" />

                                    {isLensIntelligenceEnabledFlag ? (
                                        <button
                                            type="button"
                                            className={styles.lensIntelligenceIconBtn}
                                            onClick={() => navigate('/lens-intelligence')}
                                            aria-label="Open Lens Intelligence"
                                            title="Lens Intelligence">
                                            <Sparkle16Filled className={styles.lensIntelligenceIconGlyph} />
                                        </button>
                                    ) : null}
                                </>
                            ) : null}
                            <QuickAddMenu setObsModal={setObsModal} setQuestionModal={setQuestionModal} setTaskModal={setTaskModal} setNotepadModal={setNotepadModal} />
                            <Menu>
                                <MenuTrigger disableButtonEnhancement>
                                    <div style={{ height: 27 }} className="d-inline-block" role="button">
                                        <Settings20Regular className={styles.settingLogo} color="white" />
                                    </div>
                                </MenuTrigger>
                                <MenuPopover>
                                    <MenuList>
                                        <MenuItem onClick={() => navigate('/EditProject')}>
                                            <div className="ps-1 pe-1">
                                                <Settings20Regular />
                                                <Text as="p" font="Inter" className={styles.menuLinkText}>
                                                    Project Settings
                                                </Text>
                                            </div>
                                        </MenuItem>
                                        {isProjectAdmin && (
                                            <MenuItem onClick={() => navigate('/team/vdr')}>
                                                <div className="ps-1 pe-1">
                                                    <PeopleTeam20Regular />
                                                    <Text as="p" font="Inter" className={styles.menuLinkText}>
                                                        Teams
                                                    </Text>
                                                </div>
                                            </MenuItem>
                                        )}

                                        {isProjectAdmin || selectedProject?.vdr?.nda ? (
                                            <MenuItem onClick={() => navigate(selectedProject?.vdr?.nda ? '/nda' : '/uploadnda')}>
                                                <div className="ps-1 pe-1">
                                                    <ReadingModeMobile20Regular />
                                                    <Text as="p" font="Inter" className={styles.menuLinkText}>
                                                        Project NDA
                                                    </Text>
                                                </div>
                                            </MenuItem>
                                        ) : (
                                            <MenuItem>
                                                <div className="ps-1 pe-1">
                                                    <Text as="p" font="Inter" className={styles.menuLinkText}>
                                                        No NDA uploaded
                                                    </Text>
                                                </div>
                                            </MenuItem>
                                        )}
                                    </MenuList>
                                </MenuPopover>
                            </Menu>
                        </>
                    )}
                    {showPulseMenu ? (
                        <PulseNavMenu
                            showUserPulse={showUserPulse}
                            showWorkspacePulse={showWorkspacePulse}
                            onUserPulse={openModal}
                            onWorkspacePulse={handleOpenWorkspacePulse}
                        />
                    ) : null}
                    <Menu>
                        <MenuTrigger disableButtonEnhancement>
                            <Avatar role="button" name={user?.full_name} aria-label="Guest" active="active" color="beige" />
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem onClick={() => navigate('/user-settings')}>User Settings</MenuItem>
                                {isWorkspaceSuperAdmin && effectiveWorkspaceId && (
                                    <MenuItem onClick={() => navigate(`/workspace-settings/${effectiveWorkspaceId}`)}>Workspace Settings</MenuItem>
                                )}
                                <MenuItem
                                    onClick={() => {
                                        setShowGeneralChat(true);
                                    }}>
                                    Ask AI
                                </MenuItem>
                                <MenuItem>
                                    <a href="mailto:LensSupport@beyond-ma.com" style={{ textDecoration: 'none', color: 'inherit' }}>
                                        Contact Support
                                    </a>
                                </MenuItem>
                                <MenuItem onClick={openCookiePreferences}>Cookie preferences</MenuItem>
                                {memberships.length > 1 && <MenuItem onClick={handleChangeWorkspace}>Change Workspace</MenuItem>}
                                <MenuItem onClick={handleLogout}>Logout </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>
            </div>
            <GeneralGPTChatDrawer show={showGeneralChat} setShow={setShowGeneralChat} />
            {notepadModal && <NotepadSidebar show={notepadModal} setShow={setNotepadModal} />}
            <ObservationFormSidebar show={obsModal} setShow={setObsModal} />
            <PostInformationRequestSidebar show={questionModal} setShow={setQuestionModal} />
            <TaskFormSidebar show={taskModal} setShow={setTaskModal} />
        </>
    );
};

export const QuickAddMenu = ({ setObsModal, setQuestionModal, setTaskModal, setNotepadModal }) => {
    const location = useLocation();
    const { selectedProject } = useSelector(state => state?.projectData);
    const { user } = useSelector(state => state?.user);
    return (
        selectedProject &&
        location?.pathname !== '/project' &&
        user?.has_diligence_tools && (
            <Menu style={{ marginTop: '20px' }}>
                <MenuTrigger disableButtonEnhancement>
                    <div style={{ height: 27 }} className="d-inline-block" role="button">
                        <AddCircle20Filled color="white" />
                        <ChevronDown12Regular color="white" />
                    </div>
                </MenuTrigger>
                <MenuPopover>
                    <MenuList>
                        <MenuItem onClick={() => setObsModal(true)}>
                            <Text as="p" font="Inter" className={styles.menuLinkText}>
                                Add Observation
                            </Text>
                        </MenuItem>
                        <MenuItem onClick={() => setQuestionModal(true)}>
                            <Text as="p" font="Inter" className={styles.menuLinkText}>
                                Add Information Request
                            </Text>
                        </MenuItem>
                        <MenuItem onClick={() => setTaskModal(true)}>
                            <Text as="p" font="Inter" className={styles.menuLinkText}>
                                Add Task
                            </Text>
                        </MenuItem>
                        <MenuItem onClick={() => setNotepadModal(true)}>
                            <Text as="p" font="Inter" className={styles.menuLinkText}>
                                Add Journal Entry
                            </Text>
                        </MenuItem>
                    </MenuList>
                </MenuPopover>
            </Menu>
        )
    );
};

export default Header;
